import { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import type { LineContextMenuProps } from './MasterDocument.types';

/**
 * Menú contextual flotante para acciones sobre una línea de detalle
 * (editar, duplicar, eliminar). Se posiciona en las coordenadas del clic
 * y se cierra al hacer clic fuera o presionar Escape.
 *
 * @see MasterDocument.types - LineContextMenuProps
 */
export function LineContextMenu({
  open,
  position,
  onClose,
  items,
  className,
}: LineContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside or Escape
  useEffect(() => {
    if (!open) return;

    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    }

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={menuRef}
      className={clsx(
        'fixed z-[200] min-w-[190px] py-1.5 rounded-xl bg-bg-primary',
        'shadow-lg',
        'animate-[slideDown_150ms_ease-out]',
        'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
        className,
      )}
      style={{ top: position.y, left: position.x }}
      role="menu"
    >
      {items.map((item, index) => (
        <div key={index}>
          {item.separator && (
            <div className="h-px bg-border-secondary dark:bg-dark-border-primary my-1" />
          )}
          <button
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return;
              item.onClick();
              onClose();
            }}
            className={clsx(
              'flex items-center gap-2.5 w-full px-4 py-[9px] text-sm text-left transition-all duration-150',
              item.disabled
                ? 'text-content-tertiary opacity-50 cursor-not-allowed dark:text-dark-content-secondary'
                : item.danger
                  ? 'text-error-content hover:bg-error-bg dark:hover:bg-error-content/10'
                  : 'text-content-primary hover:bg-primary-100 dark:text-dark-content-primary dark:hover:bg-dark-bg-secondary',
            )}
          >
            {item.icon && <span className="w-4 h-4 shrink-0">{item.icon}</span>}
            {item.label}
          </button>
        </div>
      ))}
    </div>
  );
}
